import {
  AGENT_MENTION_TRIGGER,
  FILE_MENTION_TRIGGER,
  RE_FILE_MENTION_UNESCAPED_PATH
} from "@/components/Markdown/mentions.ts"
import { agentUsername } from "@/hooks/useProfile.ts"
import { withGlobalLoader } from "@/lib/context.ts"

import type { CompletionDropdownItem, CompletionItemType } from "@/components/PromptInput/Completion/CompletionModel.ts"
import type { CommitContext } from "@/components/PromptInput/PromptInputModel.ts"
import type { BatchInfo } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"


export type CompletionProviderProps = {
  batchInfo: BatchInfo
  type: CompletionItemType
  value: string
}

export type CompletionProvider = (props: CompletionProviderProps) => Promise<CompletionDropdownItem[]>

const PARTIAL_MENTION_PREFIXES = [
  {
    entityText: "file",
    description: "Mention file or folder"
  }
]

const FIXED_MENTIONS: CompletionDropdownItem[] = [
  {
    type: "fixed",
    entityText: agentUsername,
    completedText: AGENT_MENTION_TRIGGER,
    description: "Mention AI agent"
  }
]

export function createCompletionProvider(queries: Queries, commitContext: CommitContext, orgContext: OrgContext | null): CompletionProvider {
  return async (props) => {
    const notPredefinedSuggestions = await loadNotPredefinedSuggestions({
      ...props,
      ...commitContext,
      orgContext,
      queries
    })
    const predefinedSuggestions = getPredefinedSuggestions(props.value, props.type)
    return [...predefinedSuggestions, ...notPredefinedSuggestions]
  }
}

type LoadCompletionSuggestionsProps = CommitContext & CompletionProviderProps & {
  queries: Queries
  orgContext: OrgContext | null
}

async function loadNotPredefinedSuggestions(props: LoadCompletionSuggestionsProps): Promise<CompletionDropdownItem[]> {
  if (props.type === "file") {
    const result = await withGlobalLoader(props.orgContext, props.queries.findFiles(props.projectKey, props.repo, props.commit, props.value, props.batchInfo))
    return result.data.map(file => {
      return {
        type: "file",
        path: file.path,
        completedText: FILE_MENTION_TRIGGER + escapeFilePathForCompletion(file.path),
        entityText: file.path
      }
    })
  } else if (props.type === "profile") {
    const profiles = await withGlobalLoader(props.orgContext, props.queries.listProfiles(props.batchInfo, props.value))
    return profiles.map(profile => {
      return {
        type: "profile",
        profile: profile,
        completedText: "@" + profile.username,
        entityText: profile.username
      }
    })
  } else {
    return Promise.resolve([])
  }
}

export function getPredefinedSuggestions(entityText: string, type: string): CompletionDropdownItem[] {
  if (type === "profile") {
    const partial: CompletionDropdownItem[] = PARTIAL_MENTION_PREFIXES.filter(partial => partial.entityText.startsWith(entityText)).map(partial => {
      return {
        type: "partial",
        completedText: `@${partial.entityText}:`,
        entityText: partial.entityText,
        description: partial.description
      }
    })
    const fixed = FIXED_MENTIONS.filter(fixed => fixed.entityText.startsWith(entityText))
    return [...fixed, ...partial]
  } else {
    return []
  }
}

function escapeFilePathForCompletion(path: string): string {
  const match = path.match(RE_FILE_MENTION_UNESCAPED_PATH)
  if (match && match[0].length === path.length) {
    return path
  } else {
    return `"${path}"`
  }
}